import { t } from "@lingui/macro";
import { Mail, Shield } from "lucide-react";
import { Link } from "react-router-dom";

import { FirstRoundLegalNotice } from "../first-round-notice";

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
  <section id={id} className="mb-10 scroll-mt-24">
    <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">{title}</h2>
    <div className="space-y-4 text-gray-600 leading-relaxed">{children}</div>
  </section>
);

const Sub = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mt-4">
    <h3 className="font-medium text-gray-800 mb-2">{title}</h3>
    <div className="space-y-2">{children}</div>
  </div>
);

const Highlight = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
    {children}
  </div>
);

const toc = [
  { id: "overview", label: "Overview" },
  { id: "data-collected", label: "Data We Collect" },
  { id: "how-we-use", label: "How We Use Your Data" },
  { id: "ai-processing", label: "AI Processing Disclosure" },
  { id: "data-sharing", label: "Data Sharing & Processors" },
  { id: "data-retention", label: "Data Retention" },
  { id: "your-rights", label: "Your Rights" },
  { id: "cookies", label: "Cookies & Tracking" },
  { id: "security", label: "Security" },
  { id: "children", label: "Children's Privacy" },
  { id: "international", label: "International Transfers" },
  { id: "changes", label: "Changes to This Policy" },
  { id: "contact", label: "Contact Us" },
];

export const PrivacyPolicyPage = () => {
  const EFFECTIVE_DATE = "15 May 2026";
  const SUPPORT_EMAIL = "support@theresume.io";
  const COMPANY_NAME = "theResume (TalentForge Innovations Pty Ltd)";

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-4">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-7 h-7 text-sky-500 shrink-0" />
          <h1 className="text-3xl font-bold text-gray-900">{t`Privacy Policy`}</h1>
        </div>
        <p className="text-sm text-gray-500 ml-10">
          {t`Effective date:`} {EFFECTIVE_DATE} · {t`Last updated:`} {EFFECTIVE_DATE}
        </p>
        <FirstRoundLegalNotice />
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 flex gap-10">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-10 bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
            <nav className="space-y-1">
              {toc.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className="block w-full text-left text-sm text-gray-600 hover:text-sky-600 py-1 transition-colors cursor-pointer"
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <Section id="overview" title="1. Overview">
            <p>
              <strong>{COMPANY_NAME}</strong> ("<strong>theResume</strong>", "we", "us", or "our") operates
              the theResume platform at <strong>theresume.io</strong>, including the web application,
              AI-powered interview tools, spotlight intro features, and recruiter services
              (collectively, the "<strong>Services</strong>").
            </p>
            <p>
              This Privacy Policy explains what personal data we collect, why we collect it, how it is processed
              (including by artificial intelligence), who it is shared with, how long we keep it, and what
              rights you have over it.
            </p>
            <p>
              By creating an account and using the Services, you confirm that you have read, understood, and
              agreed to this Policy. If you do not agree, please do not use the Services.
            </p>
            <Highlight>
              <strong>Key data retention principle:</strong> While your data exists on the theResume platform,
              you can delete it at any time (subject to the exceptions below). However, once a recruiter
              has viewed, downloaded, or exported your resume, spotlight intro, or interview recording,
              that recruiter retains a copy outside our control, and we cannot delete it from their records.
              We will mark the data as deleted in our system and stop showing it to new users.
            </Highlight>
          </Section>

          <Section id="data-collected" title="2. Data We Collect">
            <Sub title="2.1 Account & Profile Data">
              <p>When you register, we collect your name, email address, username, profile picture,
              locale preference, phone number (optional), LinkedIn URL (optional), WhatsApp number
              (optional), and authentication method (email magic link, Google OAuth).</p>
            </Sub>

            <Sub title="2.2 Resume Data">
              <p>The full content of any resume you create or upload, including work experience, education,
              skills, contact details, and any personal information contained within. We also store
              computed fields such as ATS scores, job-match scores, and AI-generated improvements.</p>
            </Sub>

            <Sub title="2.3 AI Interview Data">
              <p>When you participate in an AI-powered interview (whether a recruiter-initiated interview or
              a practice session), we collect and store:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Audio and video recordings of the session</li>
                <li>Full text transcripts of all questions and your responses</li>
                <li>AI-generated scores, feedback, and analysis of your answers</li>
                <li>Session metadata (start time, duration, question count, tab-switch events)</li>
                <li>Identity verification status</li>
              </ul>
            </Sub>

            <Sub title="2.4 Spotlight Intro Data">
              <p>When you record a Spotlight Intro (your video introduction), we collect:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>The video recording of your introduction</li>
                <li>AI-generated transcript of your spoken content</li>
                <li>Elasticsearch-indexed summary for recruiter discovery</li>
              </ul>
            </Sub>

            <Sub title="2.5 Job Application Data">
              <p>When you apply for a job through the platform, we collect your application status, applied
              date, matching scores, and any communication between you and recruiters through the platform.
              If applying via OTP-link (without an account), we collect your email and phone number
              provided during the application process.</p>
            </Sub>

            <Sub title="2.6 Payment Data">
              <p>Subscription and payment information is processed by Stripe (and Razorpay for Indian
              transactions). We store transaction references, plan details, and subscription status.
              We do not store full card numbers or CVV codes — these are handled entirely by our
              payment processors.</p>
            </Sub>

            <Sub title="2.7 Usage & Behavioural Data">
              <p>We collect how you interact with the platform, including pages visited, features used,
              clicks, form submissions, and session replays (see Section 8 — Cookies & Tracking).</p>
            </Sub>

            <Sub title="2.8 Communications">
              <p>Any messages you send to us via the contact form or support email, including the content
              and metadata of those communications.</p>
            </Sub>
          </Section>

          <Section id="how-we-use" title="3. How We Use Your Data">
            <p>We use your personal data for the following purposes:</p>
            <ul className="list-disc pl-5 space-y-2 text-sm">
              <li><strong>Providing the Services:</strong> Creating your account, storing and displaying your resume, matching you with job opportunities, enabling interviews and spotlight intros.</li>
              <li><strong>AI-Powered Features:</strong> Analysing your resume to generate improvement suggestions, ATS scores, tailored job descriptions, and matching scores. Conducting and analysing AI interviews. Generating feedback and coaching insights from practice sessions. See Section 4 for full AI disclosure.</li>
              <li><strong>Recruiter Matching:</strong> Making your profile (resume, spotlight intro, interview performance) discoverable by recruiters based on job criteria you have applied for or opted into.</li>
              <li><strong>Communications:</strong> Sending transactional emails (magic login links, application status updates, interview notifications), and platform notifications. When you accept our Terms you also agree to occasional emails from theResume and First Round, our interview practice service (product updates, interview tips and offers). We never send spam, we do not sell or share your email address with other businesses for their marketing, and every marketing email has an unsubscribe link.</li>
              <li><strong>Platform Safety & Security:</strong> Detecting abuse, enforcing our Terms of Service, preventing fraud.</li>
              <li><strong>Platform Improvement:</strong> Aggregate and anonymised analytics on feature usage to improve the platform. We do not sell individual user data.</li>
              <li><strong>Legal Compliance:</strong> Retaining transaction records and audit logs as required by applicable law.</li>
            </ul>
          </Section>

          <Section id="ai-processing" title="4. AI Processing Disclosure">
            <Highlight>
              This section explains how artificial intelligence processes your data. Please read it carefully
              before using AI-powered features.
            </Highlight>

            <Sub title="4.1 Resume Analysis & Enhancement">
              <p>
                When you use AI features (resume improvement, ATS analysis, job tailoring), your resume
                content — including your work history, personal details, and skills — is sent to
                <strong> OpenAI's API</strong> and/or <strong>Groq's API</strong> for processing.
                The AI generates suggestions, scores, and improvements based on your content.
              </p>
              <p className="text-sm mt-2">
                <strong>Data minimisation:</strong> We send only the resume data necessary for the specific
                feature. We do not send your payment data or authentication credentials to AI providers.
              </p>
            </Sub>

            <Sub title="4.2 AI-Powered Interviews">
              <p>
                When you participate in an AI interview (recruiter-initiated or practice), the following occurs:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
                <li>Your audio and video stream is transmitted in real-time to <strong>LiveKit</strong> for the session.</li>
                <li>Your voice audio is processed by <strong>OpenAI's Realtime API</strong> for speech recognition and AI response generation — the AI interviewer hears you and responds via this API.</li>
                <li>Session audio is transcribed using <strong>Groq's Whisper API</strong> and stored as a text transcript.</li>
                <li>Your interview transcript and responses are sent to <strong>OpenAI</strong> to generate structured feedback, scores, and coaching insights.</li>
                <li>The full session recording (audio and video) is stored in <strong>AWS S3</strong> and associated with your interview record.</li>
              </ul>
            </Sub>

            <Sub title="4.3 Spotlight Intro Processing">
              <p>
                When you record a Spotlight Intro video:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
                <li>Your video is captured via <strong>LiveKit</strong> and stored in <strong>AWS S3</strong>.</li>
                <li>Your speech is transcribed using <strong>OpenAI's Realtime API</strong> during the recording.</li>
                <li>The transcript is indexed in <strong>Elasticsearch</strong> to make your introduction discoverable by recruiters who are looking for candidates.</li>
              </ul>
            </Sub>

            <Sub title="4.4 AI Provider Data Policies">
              <p>We use the following AI providers. Their API usage policies apply:</p>
              <ul className="list-disc pl-5 space-y-2 text-sm mt-2">
                <li>
                  <strong>OpenAI</strong> — Data sent via the API is not used to train OpenAI's models by default,
                  per <a href="https://openai.com/policies/api-data-usage-policies" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">OpenAI's API Data Usage Policy</a>.
                  OpenAI may retain API inputs and outputs for up to 30 days for safety monitoring.
                </li>
                <li>
                  <strong>Groq</strong> — Data sent via the API is governed by
                  <a href="https://groq.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline"> Groq's Privacy Policy</a>.
                  Groq does not train models on API data.
                </li>
              </ul>
              <p className="mt-2 text-sm">
                <strong>We do not use your personal data to train our own AI models</strong>, and we do not
                sell or licence your data to AI training datasets.
              </p>
            </Sub>
          </Section>

          <Section id="data-sharing" title="5. Data Sharing & Third-Party Processors">
            <p>We share your data with the following categories of recipients:</p>

            <Sub title="5.1 Recruiters (when you apply for a job or opt in)">
              <p>
                When you apply for a job, your resume, spotlight intro (if you have one), and interview
                performance may be shared with the recruiter who posted the job. Recruiters can view, score,
                download, and export this information. Once a recruiter has downloaded or viewed your data,
                we cannot delete it from their possession.
              </p>
            </Sub>

            <Sub title="5.2 Sub-processors & Third-Party Services">
              <div className="overflow-x-auto mt-2">
                <table className="text-sm w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">Provider</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">Purpose</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">Data Shared</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      ["OpenAI", "AI interview, resume analysis, feedback", "Resume content, interview transcripts, candidate responses"],
                      ["Groq", "Audio transcription, LLM fallback", "Audio files, resume content"],
                      ["LiveKit", "Real-time video/audio rooms", "User identity, audio/video streams, session metadata"],
                      ["AWS S3", "File storage", "Resume PDFs, profile pictures, interview & spotlight recordings"],
                      ["AWS SES", "Transactional email", "Email address, email content"],
                      ["Stripe", "Payment processing", "Billing info, subscription plan, user ID"],
                      ["Razorpay", "Payment processing (India)", "Billing info, subscription plan"],
                      ["Firebase (Google)", "Analytics & performance monitoring", "Usage events, page views, device info"],
                      ["Mixpanel", "Behavioural analytics & session replay", "User ID, email, all UI interactions, session recordings"],
                      ["Sentry", "Error monitoring", "Error stack traces, request metadata"],
                      ["Elasticsearch", "Search & AI matching", "Resume content, interview transcripts, spotlight summaries"],
                    ].map(([provider, purpose, data]) => (
                      <tr key={provider} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-800">{provider}</td>
                        <td className="px-4 py-2">{purpose}</td>
                        <td className="px-4 py-2 text-gray-500">{data}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Sub>

            <Sub title="5.3 Legal Disclosures">
              <p>We may disclose your data to law enforcement or regulatory authorities if required by
              applicable law, court order, or to protect our legal rights.</p>
            </Sub>

            <Sub title="5.4 What We Do Not Do">
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>We do not sell your personal data to third parties.</li>
                <li>We do not use your data to train commercial AI models.</li>
                <li>We do not share your full resume or interview recordings with recruiters who are not
                associated with a job you have applied to.</li>
              </ul>
            </Sub>
          </Section>

          <Section id="data-retention" title="6. Data Retention">
            <Highlight>
              <strong>Important — please read carefully.</strong> This section explains how long we keep
              your data and when you can and cannot delete it.
            </Highlight>

            <Sub title="6.1 Data You Control — Can Be Deleted Anytime">
              <p>The following data can be deleted by you at any time through your account settings,
              <em>as long as it has not yet been accessed by a recruiter</em>:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
                <li><strong>Resumes</strong> — Delete individual resumes from your dashboard. Deletion removes the PDF from storage, the preview, and the searchable index.</li>
                <li><strong>Spotlight Intro</strong> — Delete your intro video from your dashboard. Deletion removes the video recording, transcript, and search index entry.</li>
                <li><strong>Practice Interview Sessions</strong> — Delete completed or draft sessions. Deletion removes the recording, transcript, and analysis.</li>
                <li><strong>Your Account</strong> — Deleting your account removes all your data from our active database, storage, and search indices, along with all of the above.</li>
              </ul>
            </Sub>

            <Sub title="6.2 The Recruiter Access Exception">
              <p>
                Once a recruiter has <strong>viewed, downloaded, or exported</strong> any of the following,
                we may not be able to fully delete it from the platform's records, and cannot delete it
                from the recruiter's own systems:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
                <li><strong>Your resume</strong> — if a recruiter has downloaded or reviewed it in the context of a job application</li>
                <li><strong>Your spotlight intro video</strong> — if a recruiter has watched it</li>
                <li><strong>Your AI interview recording and feedback</strong> — if a recruiter has viewed or downloaded your interview session</li>
              </ul>
              <p className="mt-2 text-sm">
                In these cases: we will stop showing the item to new users, mark it as deleted in our platform,
                and remove it from search results. However, the recruiter retains whatever they have
                already viewed or downloaded, and we cannot delete it from their records. This is consistent
                with how any CV or job application works in the real world.
              </p>
            </Sub>

            <Sub title="6.3 Retention After Account Deletion">
              <p>After you delete your account, we retain limited data as required by law:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
                <li><strong>Transaction records</strong> (7 years for tax and accounting compliance)</li>
                <li><strong>Anonymised usage data</strong> for platform analytics (no personal identifiers)</li>
                <li><strong>Legal hold data</strong> if required by an active legal proceeding</li>
              </ul>
            </Sub>

            <Sub title="6.4 Specific Retention Periods">
              <div className="overflow-x-auto mt-2">
                <table className="text-sm w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">Data Type</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">Retention Period</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {[
                      ["Active account data", "Until you delete your account"],
                      ["Resumes & previews", "Until you delete them (or your account)"],
                      ["Spotlight intro recordings", "Until you delete them (or your account)"],
                      ["AI interview recordings", "Until you delete them (or your account), subject to recruiter access exception"],
                      ["Practice interview recordings", "Until you delete them (or your account)"],
                      ["Payment & transaction records", "7 years (legal requirement)"],
                      ["Support communications", "3 years"],
                      ["Anonymised analytics data", "Indefinitely (no personal identifiers)"],
                    ].map(([type, period]) => (
                      <tr key={type} className="hover:bg-gray-50">
                        <td className="px-4 py-2">{type}</td>
                        <td className="px-4 py-2 text-gray-500">{period}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Sub>
          </Section>

          <Section id="your-rights" title="7. Your Rights">
            <p>Depending on your jurisdiction, you may have the following rights regarding your personal data:</p>

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {[
                { title: "Right to Access", desc: "Request a copy of all personal data we hold about you." },
                { title: "Right to Correction", desc: "Request correction of inaccurate or incomplete data." },
                { title: "Right to Deletion", desc: "Request deletion of your data, subject to retention obligations and the recruiter access exception in Section 6.2." },
                { title: "Right to Portability", desc: "Request your data in a machine-readable format (e.g., JSON export of your resume and profile)." },
                { title: "Right to Restrict Processing", desc: "Ask us to stop processing your data while a dispute is resolved." },
                { title: "Right to Object", desc: "Object to processing based on our legitimate interests." },
                { title: "Right to Opt-Out of Analytics", desc: "Opt out of non-essential tracking and session recordings (see Section 8)." },
                { title: "Right to Withdraw Consent", desc: "Where processing is based on consent, you can withdraw it at any time without affecting prior processing." },
              ].map(({ title, desc }) => (
                <div key={title} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="font-medium text-gray-900 text-sm">{title}</p>
                  <p className="text-sm text-gray-600 mt-1">{desc}</p>
                </div>
              ))}
            </div>

            <Sub title="7.1 How to Exercise Your Rights">
              <p>
                You can exercise most rights directly from your account settings:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
                <li>Delete individual resumes from your <strong>Dashboard → Resumes</strong></li>
                <li>Delete your spotlight intro from <strong>Dashboard → Spotlight Intro</strong></li>
                <li>Delete your account from <strong>Dashboard → Settings → Danger Zone</strong></li>
                <li>Request a data export by emailing <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-600 hover:underline">{SUPPORT_EMAIL}</a></li>
              </ul>
              <p className="mt-3">
                For all other rights requests (access, correction, restriction, portability), email us at{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-600 hover:underline">{SUPPORT_EMAIL}</a>{" "}
                with the subject line "<strong>Data Rights Request</strong>". We will respond within 30 days.
              </p>
            </Sub>
          </Section>

          <Section id="cookies" title="8. Cookies & Tracking">
            <Sub title="8.1 Essential Cookies">
              <p>We use session cookies and local storage for authentication (JWT access tokens, refresh tokens)
              and user preferences. These are required for the platform to function and cannot be disabled.</p>
            </Sub>

            <Sub title="8.2 Analytics & Session Replay">
              <p>
                We use <strong>Firebase Analytics</strong> (Google) and <strong>Mixpanel</strong> to understand
                how users interact with the platform. Mixpanel session replay is active at 100% capture rate,
                which means your UI interactions on the platform (including mouse movements, clicks,
                and some form inputs) may be recorded as a session video.
              </p>
              <p className="mt-2 text-sm">
                These tools collect: page views, feature interactions, button clicks, user identifiers
                (user ID, email), device and browser information, and session recordings.
              </p>
              <p className="mt-2 text-sm">
                You can opt out of analytics tracking by emailing{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-600 hover:underline">{SUPPORT_EMAIL}</a>.
                Note: opting out does not affect essential authentication cookies.
              </p>
            </Sub>

            <Sub title="8.3 Third-Party Embeds">
              <p>Pages that embed third-party content (e.g., company logos via Logo.dev) may result in
              those services receiving your IP address and browser metadata.</p>
            </Sub>
          </Section>

          <Section id="security" title="9. Security">
            <p>
              We implement industry-standard security measures including:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
              <li>All data in transit is encrypted via HTTPS/TLS</li>
              <li>Resume PDFs and other sensitive files are stored privately and accessed via signed, time-limited URLs</li>
              <li>Authentication uses short-lived JWT tokens (15 minutes) with rotating refresh tokens (2 days)</li>
              <li>Magic-link authentication — no passwords are stored</li>
              <li>File uploads are validated by magic bytes (file content) before processing</li>
              <li>API endpoints enforce ownership checks to prevent cross-user data access</li>
              <li>Error monitoring and alerting via Sentry</li>
            </ul>
            <p className="mt-4 text-sm">
              No system is 100% secure. If you discover a security vulnerability, please email us at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sky-600 hover:underline">{SUPPORT_EMAIL}</a>{" "}
              and we will address it promptly.
            </p>
          </Section>

          <Section id="children" title="10. Children's Privacy">
            <p>
              The Services are not directed at individuals under the age of 16 (or the applicable age of
              digital consent in your jurisdiction). We do not knowingly collect personal data from children.
              If you believe a child has provided us with personal data, please contact us and we will
              delete it promptly.
            </p>
          </Section>

          <Section id="international" title="11. International Data Transfers">
            <p>
              theResume operates globally. Your data may be transferred to and processed in countries
              outside your own, including Singapore, the United States, and the European Union,
              where our infrastructure and sub-processors are located.
            </p>
            <p className="mt-2 text-sm">
              Where required by law (e.g., GDPR, India's DPDP Act), we ensure appropriate safeguards are
              in place for international transfers, such as standard contractual clauses with our processors.
            </p>
          </Section>

          <Section id="changes" title="12. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we make material changes, we will
              notify you via email and/or a prominent in-app notice at least 14 days before the changes
              take effect. Continued use of the Services after the effective date constitutes acceptance
              of the updated Policy.
            </p>
          </Section>

          <Section id="contact" title="13. Contact Us">
            <p>
              For any questions, complaints, or data rights requests related to this Privacy Policy,
              please contact us:
            </p>
            <div className="mt-4 bg-gray-50 rounded-lg border border-gray-200 p-5 flex items-start gap-4">
              <Mail className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">{COMPANY_NAME}</p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-sky-600 hover:underline text-sm"
                >
                  {SUPPORT_EMAIL}
                </a>
                <p className="text-sm text-gray-500 mt-1">
                  Subject line: <em>Privacy Policy Enquiry</em> or <em>Data Rights Request</em>
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Response time: within 30 days
                </p>
              </div>
            </div>
          </Section>
        </main>
      </div>
    </div>
  );
};
